import { useCallback, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatCurrencyMinor, formatDate, formatDateTime, formatNumber } from '@/i18n/format';

function balanceOf(inv: { total_minor: string; paid_minor: string; currency: string; balance_minor?: string }, locale: 'en' | 'ru'): string {
  // The server's balance (credit and debit notes included); the old total - paid only as a fallback for an older API.
  return formatCurrencyMinor(inv.balance_minor ?? String(Number(inv.total_minor) - Number(inv.paid_minor)), inv.currency, locale);
}

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). Invoice list -> line-level detail
 * -> PDF download, per the epic's spec. The PDF is fetched as base64 JSON
 * (apps/api/src/routes/portal.ts's `GET /invoices/:id/pdf` - the same
 * `ArtifactBlobStore` shape ADR-074 established for evidence, ADR-075
 * §Part A.8) and turned into a real downloadable file client-side via an
 * object URL - there is no separate binary-download endpoint to hit.
 */
export function InvoicesTab(): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listResult = useApi(() => endpoints.invoices({}), []);
  const detailResult = useApi(() => (selectedId ? endpoints.invoiceDetail(selectedId) : Promise.resolve(null)), [selectedId]);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { t, locale } = useLanguage();

  const onDownloadPdf = useCallback(async (invoiceId: string, number: string | null) => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const { bytesBase64 } = await endpoints.invoicePdf(invoiceId);
      const bytes = Uint8Array.from(atob(bytesBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${number ?? invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(t('invoicesTab.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  }, [t]);

  if (listResult.status === 'loading') return <LoadingState />;
  if (listResult.status === 'error') return <ErrorBanner error={listResult.error} onRetry={listResult.reload} />;

  const invoices = listResult.data.rows;

  return (
    <div>
      {invoices.length === 0 ? (
        <p className="sub">{t('invoicesTab.noInvoicesYet')}</p>
      ) : (
        <div className="table-scroll"><table>
          <thead>
            <tr>
              <th>{t('invoicesTab.colInvoiceNumber')}</th>
              <th>{t('common.status')}</th>
              <th>{t('invoicesTab.colPeriod')}</th>
              <th>{t('ordersPage.colDue')}</th>
              <th style={{ textAlign: 'right' }}>{t('common.total')}</th>
              <th style={{ textAlign: 'right' }}>{t('dashboard.colBalance')}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} tabIndex={0} onClick={() => setSelectedId(inv.id)} onKeyDown={(e) => e.key === 'Enter' && setSelectedId(inv.id)}>
                <td>{inv.number ?? t('dashboard.draft')}</td>
                <td>
                  <StatusBadge value={inv.displayStatus ?? inv.state} />
                </td>
                <td>
                  {formatDate(inv.period_start, locale)} – {formatDate(inv.period_end, locale)}
                </td>
                <td>{inv.due_at ? formatDate(inv.due_at, locale) : '—'}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrencyMinor(inv.total_minor, inv.currency, locale)}</td>
                <td style={{ textAlign: 'right' }}>{balanceOf(inv, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {selectedId && (
        <div className="card" style={{ marginTop: 16 }}>
          {detailResult.status === 'loading' && <LoadingState />}
          {detailResult.status === 'error' && <ErrorBanner error={detailResult.error} onRetry={detailResult.reload} />}
          {detailResult.status === 'success' && detailResult.data && (
            <>
              <div className="page-header">
                <h2 style={{ fontSize: 15 }}>{t('invoicesTab.invoiceTitle', { number: String(detailResult.data.invoice.number ?? t('dashboard.draft')) })}</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => onDownloadPdf(detailResult.data!.invoice.id, (detailResult.data!.invoice.number as string | null) ?? null)}
                    disabled={downloading}
                  >
                    {downloading ? t('invoicesTab.preparing') : t('billing.downloadPdf')}
                  </button>
                  <button type="button" onClick={() => setSelectedId(null)}>
                    {t('invoicesTab.close')}
                  </button>
                </div>
              </div>
              {downloadError && (
                <div className="error-banner" role="alert">
                  {downloadError}
                </div>
              )}
              <div className="table-scroll"><table>
                <thead>
                  <tr>
                    <th>{t('billing.colKind')}</th>
                    <th style={{ textAlign: 'right' }}>{t('returnDetail.qty')}</th>
                    <th style={{ textAlign: 'right' }}>{t('invoicesTab.colRate')}</th>
                    <th style={{ textAlign: 'right' }}>{t('billing.colAmount')}</th>
                    <th>{t('dashboard.colOccurred')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detailResult.data.lines.map((l) => (
                    <tr key={l.id} style={{ cursor: 'default' }}>
                      <td>{statusLabel(t, l.kind)}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(l.qty, locale)}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(l.rate, locale)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrencyMinor(l.amount_minor, l.currency, locale)}</td>
                      <td>{formatDateTime(l.source_occurred_at, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
              {detailResult.data.payments.length > 0 && (
                <>
                  <h3 style={{ fontSize: 13, marginTop: 16 }}>{t('invoicesTab.payments')}</h3>
                  <ul style={{ paddingLeft: 18 }}>
                    {detailResult.data.payments.map((p, i) => (
                      <li key={i} className="sub">
                        {t('invoicesTab.paymentLine', {
                          method: statusLabel(t, String(p.method)),
                          amount: formatCurrencyMinor(String(p.amount_minor), String(p.currency), locale),
                          date: formatDate(String(p.created_at ?? p.paid_at), locale),
                        })}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
